"use client";

import Image from "next/image";
import { ArrowRight, Camera, Sun, CheckCircle, XCircle, Smartphone } from "lucide-react";
import { Button } from "@/components/ui/button";

import { FAQSection } from "@/components/landing/faq-section";
import { useLandingCta } from "@/hooks/use-landing-cta";

const ANGLE_TIPS = [
  {
    title: "Side profile (best)",
    description: "Stand parallel to the car, centered on the front wheel. This gives the AI the clearest view for accurate wheel placement.",
    quality: "best",
  },
  {
    title: "3/4 front angle",
    description: "Slightly angled view showing front and side. Works well and shows more of the car's character.",
    quality: "good",
  },
  {
    title: "3/4 rear angle",
    description: "Similar angle from behind. Good for showing rear wheel and body lines together.",
    quality: "good",
  },
  {
    title: "Direct front/rear",
    description: "Straight-on shots work but show less wheel context. Use if it's your only option.",
    quality: "ok",
  },
];

const LIGHTING_TIPS = [
  {
    icon: <Sun className="h-5 w-5" />,
    title: "Natural daylight",
    description: "Overcast days are perfect-soft, even lighting without harsh shadows. Early morning or late afternoon sun also works well.",
  },
  {
    icon: <Camera className="h-5 w-5" />,
    title: "Avoid direct sun",
    description: "Midday sun creates dark shadows under the car and on wheels. If unavoidable, position so shadows fall away from the camera.",
  },
  {
    icon: <Smartphone className="h-5 w-5" />,
    title: "Indoor works too",
    description: "Garage or showroom shots work fine. Just ensure the wheel area is well-lit and visible.",
  },
];

const DO_DONT = [
  {
    type: "do",
    items: [
      "Include the full wheel and tire in frame",
      "Keep the car level (not on a steep slope)",
      "Use landscape orientation for side shots",
      "Make sure wheels are clean and visible",
      "Take multiple angles if unsure",
    ],
  },
  {
    type: "dont",
    items: [
      "Crop off part of the wheel or tire",
      "Use heavily filtered or edited photos",
      "Shoot in very low light or at night",
      "Include large obstructions blocking wheels",
      "Use extremely wide-angle distorted shots",
    ],
  },
];

const PHOTO_FAQS = [
  {
    question: "Does photo quality matter?",
    answer:
      "Resolution matters less than you'd think-phone photos work great. What matters more is wheel visibility, lighting, and angle. A clear phone shot beats a blurry DSLR photo.",
  },
  {
    question: "Can I use photos from my phone's camera roll?",
    answer:
      "Absolutely. If you have existing photos of your car that show the wheels clearly, use them. No need to take new photos unless the wheels are obscured or the lighting is poor.",
  },
  {
    question: "What if my car is dirty?",
    answer:
      "Dirty body panels are fine-the AI focuses on wheel placement. But try to have relatively clean wheels so the AI can see where the current wheel ends and the tire begins.",
  },
  {
    question: "Do I need to show both wheels?",
    answer:
      "Showing both front and rear wheels helps the AI render both accurately. Single-wheel shots work but may result in only one wheel being replaced in the preview.",
  },
  {
    question: "Can I use a photo from a car listing?",
    answer:
      "Yes, if the image quality is decent and wheels are visible. Photos from dealership listings, classified ads, or manufacturer websites all work.",
  },
];

export function PhotoTipsContent() {
  const { handleTryItNow } = useLandingCta();

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: "Photo Tips for Wheel Visualization",
    description: "Learn how to take the best car photos for wheel visualization.",
    url: "https://thewheelvisualizer.com/photo-tips",
    author: {
      "@type": "Organization",
      name: "The Wheel Visualizer",
    },
    publisher: {
      "@type": "Organization",
      name: "The Wheel Visualizer",
      url: "https://thewheelvisualizer.com",
    },
  };

  const howToSchema = {
    "@context": "https://schema.org",
    "@type": "HowTo",
    name: "How to take photos for wheel visualization",
    description: "Step-by-step guide to photographing your car for the best wheel preview results.",
    step: [
      {
        "@type": "HowToStep",
        name: "Choose your angle",
        text: "Stand parallel to the car for a side profile shot, or use a 3/4 angle for more context.",
      },
      {
        "@type": "HowToStep",
        name: "Check your lighting",
        text: "Shoot in natural daylight, preferably overcast. Avoid harsh midday shadows.",
      },
      {
        "@type": "HowToStep",
        name: "Frame the shot",
        text: "Include the full wheel and tire in frame. Use landscape orientation for side shots.",
      },
      {
        "@type": "HowToStep",
        name: "Take the photo",
        text: "Keep the camera steady and ensure the wheel area is in focus.",
      },
    ],
  };

  return (
    <div className="bg-[#04060A] text-white">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(howToSchema) }}
      />

      {/* Hero - Editorial style */}
      <section className="pt-32 pb-16 px-6">
        <div className="max-w-4xl mx-auto">
          <p className="text-xs uppercase tracking-[0.3em] text-white/40 mb-6">Guide</p>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
            Photo tips for better wheel previews
          </h1>
          <p className="text-xl text-white/60 leading-relaxed max-w-2xl">
            The right photo makes all the difference. Here&apos;s how to capture your car for the most realistic wheel visualization results.
          </p>
        </div>
      </section>

      {/* Quick summary */}
      <section className="py-12 px-6 border-y border-white/5">
        <div className="max-w-4xl mx-auto">
          <div className="grid sm:grid-cols-3 gap-8 text-center">
            <div>
              <div className="text-2xl font-semibold text-white mb-1">Side profile</div>
              <div className="text-sm text-white/50">Best angle</div>
            </div>
            <div>
              <div className="text-2xl font-semibold text-white mb-1">Daylight</div>
              <div className="text-sm text-white/50">Best lighting</div>
            </div>
            <div>
              <div className="text-2xl font-semibold text-white mb-1">Full wheel visible</div>
              <div className="text-sm text-white/50">Key requirement</div>
            </div>
          </div>
        </div>
      </section>

      {/* Angles section */}
      <section className="py-24 px-6 bg-[#080A0D]">
        <div className="max-w-5xl mx-auto">
          <div className="mb-16">
            <p className="text-xs uppercase tracking-[0.3em] text-white/40 mb-4">Camera position</p>
            <h2 className="text-3xl md:text-4xl font-semibold text-white">
              Best angles for wheel shots
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {ANGLE_TIPS.map((tip) => (
              <div
                key={tip.title}
                className={`p-6 rounded-xl border ${
                  tip.quality === "best"
                    ? "border-white/20 bg-white/[0.04]"
                    : "border-white/5 bg-white/[0.02]"
                }`}
              >
                <div className="flex items-center gap-3 mb-3">
                  <h3 className="text-lg font-semibold text-white">{tip.title}</h3>
                  {tip.quality === "best" && (
                    <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-white/10 text-white/80">
                      Recommended
                    </span>
                  )}
                </div>
                <p className="text-white/60 leading-relaxed">{tip.description}</p>
              </div>
            ))}
          </div>

          {/* Visual examples - Best vs Good */}
          <div className="mt-12 grid md:grid-cols-2 gap-6">
            {/* Best - Side profile */}
            <div>
              <div className="relative aspect-[16/10] rounded-xl overflow-hidden border border-white/20">
                <Image
                  src="/showcase/images/before4.jpg"
                  alt="Best example - side profile shot"
                  fill
                  className="object-cover"
                  sizes="(max-width: 768px) 100vw, 50vw"
                />
                <div className="absolute top-4 left-4 flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/90 text-black text-sm font-medium">
                  <CheckCircle className="h-4 w-4" />
                  Best angle
                </div>
              </div>
              <p className="text-white/70 mt-4 text-sm">
                Side profile: Full wheel visibility, clear boundaries for wheel detection.
              </p>
            </div>

            {/* Good - 3/4 angle */}
            <div>
              <div className="relative aspect-[16/10] rounded-xl overflow-hidden border border-white/10">
                <Image
                  src="/steps/car-sample.webp"
                  alt="Good example - 3/4 angle shot"
                  fill
                  className="object-cover"
                  sizes="(max-width: 768px) 100vw, 50vw"
                />
                <div className="absolute top-4 left-4 flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/70 text-black text-sm font-medium">
                  <CheckCircle className="h-4 w-4" />
                  Good angle
                </div>
              </div>
              <p className="text-white/50 mt-4 text-sm">
                3/4 angle: Shows more car character, still works well for visualization.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Lighting section */}
      <section className="py-24 px-6 bg-[#05070C] border-t border-white/5">
        <div className="max-w-5xl mx-auto">
          <div className="mb-16">
            <p className="text-xs uppercase tracking-[0.3em] text-white/40 mb-4">Lighting</p>
            <h2 className="text-3xl md:text-4xl font-semibold text-white">
              Getting the light right
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {LIGHTING_TIPS.map((tip) => (
              <div key={tip.title}>
                <div className="w-12 h-12 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center mb-5 text-white/70">
                  {tip.icon}
                </div>
                <h3 className="text-xl font-semibold text-white mb-3">{tip.title}</h3>
                <p className="text-white/60 leading-relaxed">{tip.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Do's and Don'ts */}
      <section className="py-24 px-6 bg-[#080A0D] border-t border-white/5">
        <div className="max-w-5xl mx-auto">
          <div className="mb-16">
            <p className="text-xs uppercase tracking-[0.3em] text-white/40 mb-4">Quick reference</p>
            <h2 className="text-3xl md:text-4xl font-semibold text-white">
              Do&apos;s and don&apos;ts
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {DO_DONT.map((section) => (
              <div key={section.type}>
                <div className={`flex items-center gap-2 mb-6 ${section.type === "do" ? "text-white" : "text-white/60"}`}>
                  {section.type === "do" ? (
                    <CheckCircle className="h-5 w-5" />
                  ) : (
                    <XCircle className="h-5 w-5" />
                  )}
                  <span className="text-lg font-semibold uppercase tracking-wide">
                    {section.type === "do" ? "Do" : "Don't"}
                  </span>
                </div>
                <ul className="space-y-4">
                  {section.items.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <div className={`w-1.5 h-1.5 rounded-full mt-2 ${section.type === "do" ? "bg-white/60" : "bg-white/30"}`} />
                      <span className={section.type === "do" ? "text-white/80" : "text-white/50"}>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Checklist */}
      <section className="py-24 px-6 bg-[#05070C] border-t border-white/5">
        <div className="max-w-3xl mx-auto">
          <div className="p-8 md:p-12 rounded-2xl bg-white/[0.02] border border-white/10">
            <h2 className="text-2xl font-semibold text-white mb-6">Quick checklist</h2>
            <div className="space-y-4">
              {[
                "Full wheel and tire visible in frame",
                "Side profile or 3/4 angle",
                "Good natural lighting (daylight preferred)",
                "Nothing blocking the wheel wells",
                "Car is level, not on a steep slope",
                "Wheels reasonably clean",
              ].map((item, idx) => (
                <label key={idx} className="flex items-center gap-3 text-white/70 cursor-pointer group">
                  <div className="w-5 h-5 rounded border border-white/20 flex items-center justify-center group-hover:border-white/40 transition-colors">
                    <CheckCircle className="h-3.5 w-3.5 text-white/0 group-hover:text-white/20" />
                  </div>
                  {item}
                </label>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 px-6 bg-[#04060A]">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-semibold text-white">
              Common questions
            </h2>
          </div>
          <FAQSection faqs={PHOTO_FAQS} />
        </div>
      </section>

      {/* CTA - soft, not pricing */}
      <section className="py-24 px-6 bg-[#080A0D] border-t border-white/5">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl font-semibold text-white mb-4">
            Ready to try it?
          </h2>
          <p className="text-white/60 mb-8">
            Now that you know how to take the perfect photo, see how new wheels will look on your car.
          </p>
          <Button
            size="lg"
            onClick={handleTryItNow}
            className="bg-white text-black hover:bg-white/90 font-semibold h-12 px-8"
          >
            Upload Your Photo
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </section>
    </div>
  );
}
